import {
  ConnectSmtpMailboxBody,
  EMAIL_ACCOUNT_PROVIDER_LABELS,
  type EmailMailboxDTO,
  type EmailMailboxesResponse,
  type MailboxVerifyResult,
  type MicrosoftConnectInfoDTO,
  type UpdateMailboxBody,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, ExternalLink, Info, LogIn, Mail, MailCheck, Plug, Trash2 } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Badge, Button, Card, CardBody, CardHeader, Dialog, Field, IconButton, Input, Switch, useConfirm, useToast } from '../../../components/ui';
import { useAuth } from '../../../hooks/useAuth';
import { useNow } from '../../../hooks/useNow';
import { api, errorMessage, fieldErrors } from '../../../lib/api';
import { formatDateTime, formatRelative } from '../../../lib/format';

/** Query key shared with the composer, so connecting a mailbox refreshes both places. */
export const MAILBOXES_QUERY_KEY = ['settings', 'email-mailboxes'] as const;

export function useMailboxes() {
  return useQuery({
    queryKey: MAILBOXES_QUERY_KEY,
    queryFn: ({ signal }) => api.get<EmailMailboxesResponse>('/api/settings/email-mailboxes', { signal }),
  });
}

const OUTREACH_LABEL = 'Also send automated and bulk email from this mailbox';
const OUTREACH_HINT = 'New leads are spread across the mailboxes that have this on, and each lead keeps getting email from the same mailbox.';

/**
 * Mailboxes Hearth sends from and reads replies from. Gmail (app password) and Microsoft 365 / Outlook (Microsoft
 * sign-in, when the server has an app registration) send and read replies; any other account works with its SMTP/IMAP
 * servers; Google sign-in (when the server has an OAuth app) only sends.
 */
export function MailboxesCard() {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const now = useNow();
  const [params, setParams] = useSearchParams();
  const { data } = useMailboxes();
  const [showGmail, setShowGmail] = useState(false);
  const [showMicrosoft, setShowMicrosoft] = useState(false);
  // Inline panel under the buttons: the "Other email account" form, or the Microsoft setup steps when sign-in isn't set up.
  const [panel, setPanel] = useState<'other' | 'microsoft-setup' | null>(null);

  // Google and Microsoft send the browser back here with the outcome.
  useEffect(() => {
    const outcome = params.get('mailbox');
    if (!outcome) return;
    if (outcome === 'connected') toast.success(`Mailbox connected — your email will be sent from ${params.get('address') ?? 'that address'}`);
    else toast.error(`Couldn’t connect the mailbox: ${params.get('reason') ?? 'unknown error'}`);
    const next = new URLSearchParams(params);
    for (const key of ['mailbox', 'address', 'reason']) next.delete(key);
    setParams(next, { replace: true });
  }, [params, setParams, toast]);

  const openMicrosoft = () => {
    setShowGmail(false);
    if (data?.microsoft.configured) setShowMicrosoft(true);
    else setPanel(panel === 'microsoft-setup' ? null : 'microsoft-setup');
  };
  const openOther = () => {
    setShowGmail(false);
    setPanel('other');
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: MAILBOXES_QUERY_KEY });
  const onError = (error: unknown) => toast.error(errorMessage(error));

  const startGoogle = useMutation({
    mutationFn: () => api.post<{ url: string }>('/api/settings/email-mailboxes/google/start'),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError,
  });
  const verify = useMutation({
    mutationFn: (id: string) => api.post<MailboxVerifyResult>(`/api/settings/email-mailboxes/${id}/verify`),
    onSuccess: async (result) => {
      if (result.ok) toast.success(result.message);
      else toast.error(result.message);
      await refresh();
    },
    onError,
  });
  const test = useMutation({
    mutationFn: ({ id, to }: { id: string; to: string }) => api.post<MailboxVerifyResult>(`/api/settings/email-mailboxes/${id}/test`, { to }),
    onSuccess: async (result) => {
      if (result.ok) toast.success(result.message);
      else toast.error(`Could not send: ${result.message}`);
      await refresh();
    },
    onError,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateMailboxBody }) => api.patch<EmailMailboxDTO>(`/api/settings/email-mailboxes/${id}`, body),
    onSuccess: async (mailbox) => {
      toast.success(mailbox.useForOutreach ? `${mailbox.emailAddress} now also sends automated and bulk email` : `${mailbox.emailAddress} no longer sends automated and bulk email`);
      await refresh();
    },
    onError,
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => api.delete(`/api/settings/email-mailboxes/${id}`),
    onSuccess: async () => {
      toast.success('Mailbox disconnected');
      await refresh();
    },
    onError,
  });

  const connected = async (mailbox: EmailMailboxDTO) => {
    toast.success(mailbox.canReceive ? `${mailbox.emailAddress} connected — it sends your email and reads the replies` : `${mailbox.emailAddress} connected`);
    setShowGmail(false);
    setPanel(null);
    await refresh();
  };

  const items = data?.items ?? [];
  return (
    <Card>
      <CardHeader
        title="Mailboxes"
        description="Connect a Gmail or Microsoft 365 / Outlook account (or any other mailbox) and Hearth sends your email from it and reads the replies. When a lead answers, it moves to Responded — or Potentially interested when the reply sounds keen — on its own."
      />
      <CardBody className="space-y-4 text-[13px]">
        {items.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
            {items.map((mailbox) => (
              <li key={mailbox.id} className="space-y-2 px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <MailCheck className="size-4 shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-900">{mailbox.emailAddress}</p>
                    <p className="truncate text-xs text-slate-500">
                      {mailbox.provider !== 'smtp' && `${EMAIL_ACCOUNT_PROVIDER_LABELS[mailbox.provider]} · `}
                      {mailbox.displayName ?? 'No name set'}
                      {!mailbox.isMine && ` · belongs to ${mailbox.ownerName ?? 'another user'}`}
                      {mailbox.lastUsedAt ? ` · last sent ${formatRelative(mailbox.lastUsedAt, now)}` : ' · nothing sent yet'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {mailbox.status === 'error' ? (
                      <Badge color="red">Needs attention</Badge>
                    ) : (
                      <Badge color="green" dot>
                        Sends
                      </Badge>
                    )}
                    <ReadsRepliesBadge mailbox={mailbox} now={now} />
                  </div>
                  <div className="ml-auto flex gap-1">
                    <Button size="sm" variant="ghost" loading={verify.isPending && verify.variables === mailbox.id} onClick={() => verify.mutate(mailbox.id)}>
                      Check
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={test.isPending && test.variables.id === mailbox.id}
                      onClick={() => test.mutate({ id: mailbox.id, to: mailbox.emailAddress })}
                    >
                      Send test
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={Trash2}
                      onClick={() =>
                        void (async () => {
                          if (
                            await confirm({
                              title: `Disconnect ${mailbox.emailAddress}?`,
                              message: 'Hearth stops sending from it and stops reading its replies. Its leads get their next email from another mailbox, or from the company address.',
                              confirmLabel: 'Disconnect',
                              danger: true,
                            })
                          )
                            disconnect.mutate(mailbox.id);
                        })()
                      }
                    >
                      Disconnect
                    </Button>
                  </div>
                </div>
                {mailbox.lastError && (
                  <p className="text-xs text-red-700">
                    Sending failed: {mailbox.lastError}{' '}
                    <span className="text-slate-500">
                      Fix it, then press Check —{' '}
                      {mailbox.provider === 'microsoft' ? (
                        <ReconnectMicrosoft onClick={openMicrosoft} />
                      ) : mailbox.provider === 'google' ? (
                        'if it keeps failing, connect it again with Google.'
                      ) : (
                        'for Gmail, create a new app password and connect the account again.'
                      )}
                    </span>
                  </p>
                )}
                {mailbox.canReceive && mailbox.syncError && (
                  <p className="text-xs text-red-700">
                    Couldn’t read replies: {mailbox.syncError}{' '}
                    <span className="text-slate-500">
                      Hearth tries again every minute; if this keeps happening,{' '}
                      {mailbox.provider === 'microsoft' ? <ReconnectMicrosoft onClick={openMicrosoft} /> : 'connect the account again with a new app password.'}
                    </span>
                  </p>
                )}
                <Switch
                  size="sm"
                  label="Automated & bulk email"
                  description={mailbox.useForOutreach ? 'Sequences and bulk sends can go out from this mailbox.' : 'Only one-to-one email you write goes out from this mailbox.'}
                  checked={mailbox.useForOutreach}
                  disabled={update.isPending && update.variables.id === mailbox.id}
                  onChange={(useForOutreach) => update.mutate({ id: mailbox.id, body: { useForOutreach } })}
                  className="max-w-md"
                />
              </li>
            ))}
          </ul>
        )}

        <p className="text-xs text-slate-500">
          Email you write yourself goes out from your mailbox the lead last heard from (otherwise your least-used one). Automated and bulk email only uses
          mailboxes with “Automated & bulk email” on: each lead keeps getting email from the same mailbox and new leads take turns between them. With none
          on, it goes from the company address below.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button variant="primary" icon={Mail} onClick={() => setShowGmail(true)}>
            Connect a Gmail account
          </Button>
          <Button icon={LogIn} aria-expanded={data?.microsoft.configured ? undefined : panel === 'microsoft-setup'} onClick={openMicrosoft}>
            Connect a Microsoft 365 / Outlook account
          </Button>
          <Button onClick={() => setPanel(panel === 'other' ? null : 'other')}>{panel === 'other' ? 'Cancel' : 'Other email account'}</Button>
          {data?.google.configured && (
            <Button variant="ghost" icon={Plug} loading={startGoogle.isPending} onClick={() => startGoogle.mutate()}>
              Sign in with Google (sends only, doesn’t read replies)
            </Button>
          )}
        </div>

        {panel === 'microsoft-setup' && data && !data.microsoft.configured && <MicrosoftSetupNotice info={data.microsoft} />}
        {panel === 'other' && <OtherMailboxForm onConnected={connected} />}

        <p className="text-xs text-slate-500">
          Gmail sends about 500 emails a day per account (about 2,000 with Google Workspace), and Google frowns on mass mailing from it — keep bulk sends small
          or leave them on the company address.
        </p>
      </CardBody>
      <GmailDialog open={showGmail} onClose={() => setShowGmail(false)} onConnected={connected} onUseMicrosoft={openMicrosoft} onUseOther={openOther} />
      <MicrosoftDialog open={showMicrosoft} onClose={() => setShowMicrosoft(false)} />
    </Card>
  );
}

/** Inline link in a Microsoft mailbox's error hint: Microsoft sign-in again refreshes its access (same address, same row). */
function ReconnectMicrosoft({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="font-medium text-ember-700 hover:underline" onClick={onClick}>
      Connect it again with Microsoft.
    </button>
  );
}

/** Microsoft sign-in isn't set up on this server: the app-registration steps for admins, a pointer for everyone else. */
function MicrosoftSetupNotice({ info }: { info: MicrosoftConnectInfoDTO }) {
  const { can } = useAuth();
  const toast = useToast();
  if (!can('settings:manage')) {
    return (
      <p role="status" className="flex items-start gap-1.5 rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-700">
        <Info className="mt-0.5 size-4 shrink-0 text-slate-400" /> Ask an administrator to set up Microsoft sign-in.
      </p>
    );
  }
  const copy = () => {
    navigator.clipboard.writeText(info.redirectUri).then(
      () => toast.success('Redirect URI copied'),
      () => toast.error('Couldn’t copy — select the address and copy it by hand.'),
    );
  };
  return (
    <div role="status" className="space-y-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-sky-900">
      <p className="flex items-center gap-1.5 font-medium">
        <Info className="size-4 shrink-0" /> Microsoft sign-in isn’t set up on this server yet
      </p>
      {info.setup && (
        <ol className="list-decimal space-y-0.5 pl-5">
          {info.setup.split('\n').map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ol>
      )}
      <div className="space-y-1">
        <p className="text-xs font-medium">Redirect URI</p>
        <div className="flex items-center gap-1 rounded-md border border-sky-200 bg-surface py-0.5 pr-0.5 pl-2">
          <code className="min-w-0 flex-1 text-xs break-all text-slate-700">{info.redirectUri}</code>
          <IconButton icon={Copy} size="sm" label="Copy the redirect URI" onClick={copy} />
        </div>
      </div>
    </div>
  );
}

const MICROSOFT_FORM_ID = 'microsoft-connect-form';

/** Microsoft 365 / Outlook: sign in with Microsoft; Hearth sends and reads replies through Microsoft Graph. */
function MicrosoftDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [useForOutreach, setUseForOutreach] = useState(true);
  const start = useMutation({
    mutationFn: (body: { useForOutreach: boolean }) => api.post<{ url: string }>('/api/settings/email-mailboxes/microsoft/start', body),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const busy = start.isPending;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    start.mutate({ useForOutreach });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dismissible={!busy}
      title="Connect a Microsoft 365 / Outlook account"
      description="Hearth sends email from this mailbox and reads the replies to it."
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form={MICROSOFT_FORM_ID} variant="primary" icon={LogIn} loading={busy}>
            Sign in with Microsoft
          </Button>
        </div>
      }
    >
      <form id={MICROSOFT_FORM_ID} className="space-y-4 text-[13px]" onSubmit={onSubmit} noValidate>
        <Switch checked={useForOutreach} onChange={setUseForOutreach} label={OUTREACH_LABEL} description={OUTREACH_HINT} />
        <p className="text-slate-700">You’ll sign in with Microsoft. If your company uses Microsoft 365, an administrator may need to approve Hearth the first time.</p>
        <p className="text-xs text-slate-500">Only email that arrives after you connect is read. Hearth never marks, moves or deletes your email.</p>
      </form>
    </Dialog>
  );
}

function ReadsRepliesBadge({ mailbox, now }: { mailbox: EmailMailboxDTO; now: Date }) {
  if (!mailbox.canReceive) {
    return (
      <Badge
        color="slate"
        title={mailbox.provider === 'google' ? 'Google sign-in only allows sending. Connect it again as a Gmail account (app password) to read replies.' : 'Add the incoming mail (IMAP) server to read replies.'}
      >
        Doesn’t read replies
      </Badge>
    );
  }
  if (mailbox.syncError) return <Badge color="red">Can’t read replies</Badge>;
  return (
    <Badge color="green" dot title={mailbox.lastSyncedAt ? `Last checked ${formatDateTime(mailbox.lastSyncedAt)}` : undefined}>
      Reads replies · {mailbox.lastSyncedAt ? `checked ${formatRelative(mailbox.lastSyncedAt, now)}` : 'not checked yet'}
    </Badge>
  );
}

/** Zod issues → field errors keyed by the form's field names (the login is the address in the Gmail form). */
function issueErrors(body: unknown, rename: Record<string, string> = {}): Record<string, string> {
  const parsed = ConnectSmtpMailboxBody.safeParse(body);
  const errors: Record<string, string> = {};
  if (parsed.success) return errors;
  for (const issue of parsed.error.issues) {
    const path = issue.path.join('.');
    errors[rename[path] ?? path] ??= issue.message;
  }
  return errors;
}

function useConnectMailbox(onConnected: (mailbox: EmailMailboxDTO) => Promise<void>, rename: Record<string, string> = {}) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (body: ConnectSmtpMailboxBody) => api.post<EmailMailboxDTO>('/api/settings/email-mailboxes/smtp', body),
    onSuccess: onConnected,
    onError: (error) => {
      const byField = Object.fromEntries(Object.entries(fieldErrors(error)).map(([key, message]) => [rename[key] ?? key, message]));
      setErrors(byField);
      // A failed sign-in (wrong password, unreachable server) is a 422 without field details: show it in the form.
      setFormError(Object.keys(byField).length === 0 ? errorMessage(error) : null);
    },
  });
  const submit = (body: ConnectSmtpMailboxBody, extra: Record<string, string> = {}) => {
    setFormError(null);
    const next = { ...issueErrors(body, rename), ...extra };
    setErrors(next);
    if (Object.keys(next).length === 0) mutation.mutate(body);
  };
  const reset = () => {
    setErrors({});
    setFormError(null);
  };
  return { submit, reset, errors, formError, pending: mutation.isPending };
}

function FormError({ message, hint }: { message: string | null; hint: string }) {
  if (!message) return null;
  return (
    <div role="alert" className="space-y-1 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-800">
      <p className="break-words">{message}</p>
      <p className="text-xs">{hint}</p>
    </div>
  );
}

const GMAIL_FORM_ID = 'gmail-connect-form';
const EMPTY_GMAIL = { emailAddress: '', displayName: '', password: '', useForOutreach: true };

/** A whole address is typed and it isn't a consumer Gmail one (a Google Workspace address, or another provider). */
function isNonGmailAddress(address: string): boolean {
  const domain = /^[^@\s]+@([^@\s]+\.[^@\s]+)$/.exec(address.trim())?.[1]?.toLowerCase();
  return domain !== undefined && domain !== 'gmail.com' && domain !== 'googlemail.com';
}

type GmailDialogProps = {
  open: boolean;
  onClose: () => void;
  onConnected: (mailbox: EmailMailboxDTO) => Promise<void>;
  onUseMicrosoft: () => void;
  onUseOther: () => void;
};

/** Gmail with an app password: SMTP to send and IMAP to read replies, no Google Cloud project needed. */
function GmailDialog({ open, onClose, onConnected, onUseMicrosoft, onUseOther }: GmailDialogProps) {
  const [form, setForm] = useState(EMPTY_GMAIL);
  const connect = useConnectMailbox(
    async (mailbox) => {
      setForm(EMPTY_GMAIL);
      await onConnected(mailbox);
    },
    { user: 'emailAddress' },
  );
  const close = () => {
    connect.reset();
    onClose();
  };
  const switchTo = (next: () => void) => {
    close();
    next();
  };
  const otherHint = isNonGmailAddress(form.emailAddress) ? (
    <>
      Not a Gmail address? Google Workspace addresses work here. If your company email runs on Microsoft 365 / Outlook, use{' '}
      <button type="button" className="font-medium text-ember-700 hover:underline" onClick={() => switchTo(onUseMicrosoft)}>
        “Connect a Microsoft 365 / Outlook account”
      </button>
      . For other providers use{' '}
      <button type="button" className="font-medium text-ember-700 hover:underline" onClick={() => switchTo(onUseOther)}>
        “Other email account”
      </button>
      .
    </>
  ) : (
    'Google Workspace addresses work too.'
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const address = form.emailAddress.trim();
    // Google shows app passwords as four groups of four letters; the spaces are not part of it.
    const password = form.password.replace(/\s+/g, '');
    connect.submit(
      {
        emailAddress: address,
        displayName: form.displayName.trim() || null,
        host: 'smtp.gmail.com',
        port: 465,
        secure: true,
        user: address,
        password,
        imap: { host: 'imap.gmail.com', port: 993, secure: true },
        useForOutreach: form.useForOutreach,
      },
      password !== '' && password.length !== 16 ? { password: 'An app password is 16 letters. Copy it again from Google.' } : {},
    );
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      dismissible={!connect.pending}
      title="Connect a Gmail account"
      description="Hearth sends email from this address and reads the replies to it."
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button onClick={close} disabled={connect.pending}>
            Cancel
          </Button>
          <Button type="submit" form={GMAIL_FORM_ID} variant="primary" loading={connect.pending}>
            Connect
          </Button>
        </div>
      }
    >
      <form id={GMAIL_FORM_ID} className="space-y-4 text-[13px]" onSubmit={onSubmit} noValidate>
        <ol className="list-decimal space-y-1 rounded-md bg-slate-50 py-2.5 pr-3 pl-8 text-slate-700">
          <li>
            Turn on{' '}
            <a className="text-ember-700 hover:underline" href="https://myaccount.google.com/signinoptions/twosv" target="_blank" rel="noreferrer">
              2-Step Verification <ExternalLink className="inline size-3" />
            </a>{' '}
            for the Google account.
          </li>
          <li>
            Create an{' '}
            <a className="text-ember-700 hover:underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">
              app password <ExternalLink className="inline size-3" />
            </a>{' '}
            named “Hearth”.
          </li>
          <li>Paste it below — with or without the spaces.</li>
        </ol>
        <Field label="Gmail address" hint={otherHint} error={connect.errors.emailAddress} required>
          <Input type="email" autoComplete="off" value={form.emailAddress} onChange={(e) => setForm({ ...form, emailAddress: e.target.value })} placeholder="marie@gmail.com" autoFocus />
        </Field>
        <Field label="Name shown to recipients" error={connect.errors.displayName}>
          <Input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="Marie Côté" />
        </Field>
        <Field label="App password" hint="Not your Google password: the 16 letters Google shows once." error={connect.errors.password} required>
          <Input type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="abcd efgh ijkl mnop" />
        </Field>
        <Switch checked={form.useForOutreach} onChange={(useForOutreach) => setForm({ ...form, useForOutreach })} label={OUTREACH_LABEL} description={OUTREACH_HINT} />
        <FormError message={connect.formError} hint="Check the address, and paste an app password — your usual Google password doesn’t work here. If in doubt, create a new app password." />
        <p className="text-xs text-slate-500">Hearth signs in once now to check, then stores the password encrypted. Only email that arrives after you connect is read.</p>
      </form>
    </Dialog>
  );
}

/** `smtp.example.com` → `imap.example.com`: most providers name their servers this way. */
function guessImapHost(smtpHost: string): string {
  const host = smtpHost.trim();
  return host.startsWith('smtp.') ? `imap.${host.slice(5)}` : '';
}

const EMPTY_OTHER = {
  emailAddress: '',
  displayName: '',
  host: '',
  port: '465',
  secure: true,
  user: '',
  password: '',
  readReplies: true,
  imapHost: '',
  imapPort: '993',
  imapSecure: true,
  useForOutreach: false,
};

/** Any mailbox with a password: SMTP to send and, optionally, IMAP to read replies with the same login. */
function OtherMailboxForm({ onConnected }: { onConnected: (mailbox: EmailMailboxDTO) => Promise<void> }) {
  const [form, setForm] = useState(EMPTY_OTHER);
  const connect = useConnectMailbox(async (mailbox) => {
    setForm(EMPTY_OTHER);
    await onConnected(mailbox);
  }, { 'imap.host': 'imapHost', 'imap.port': 'imapPort' });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    connect.submit({
      emailAddress: form.emailAddress.trim(),
      displayName: form.displayName.trim() || null,
      host: form.host.trim(),
      port: Number(form.port),
      secure: form.secure,
      user: form.user.trim() || form.emailAddress.trim(),
      password: form.password,
      imap: form.readReplies ? { host: form.imapHost.trim(), port: Number(form.imapPort), secure: form.imapSecure } : null,
      useForOutreach: form.useForOutreach,
    });
  };

  return (
    <form className="space-y-4 rounded-md border border-slate-200 p-3" onSubmit={onSubmit} noValidate>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Email address" hint="The address recipients see." error={connect.errors.emailAddress} required>
          <Input type="email" value={form.emailAddress} onChange={(e) => setForm({ ...form, emailAddress: e.target.value })} />
        </Field>
        <Field label="Name shown to recipients" error={connect.errors.displayName}>
          <Input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="Marie Côté" />
        </Field>
        <Field label="Login" hint="Leave empty to use the email address." error={connect.errors.user}>
          <Input autoComplete="off" value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} placeholder={form.emailAddress} />
        </Field>
        <Field label="Password or app password" hint="Used for both sending and reading." error={connect.errors.password} required>
          <Input type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
      </div>

      <fieldset className="space-y-3">
        <legend className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">Outgoing mail (SMTP) — to send</legend>
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem]">
          <Field label="Server" error={connect.errors.host} required>
            <Input
              value={form.host}
              onChange={(e) =>
                setForm({ ...form, host: e.target.value, imapHost: form.imapHost === guessImapHost(form.host) ? guessImapHost(e.target.value) : form.imapHost })
              }
              placeholder="smtp.example.com"
            />
          </Field>
          <Field label="Port" error={connect.errors.port} required>
            <Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} />
          </Field>
        </div>
        <Switch
          checked={form.secure}
          onChange={(secure) => setForm({ ...form, secure, port: form.port === (secure ? '587' : '465') ? (secure ? '465' : '587') : form.port })}
          label="Use TLS from the start (port 465)"
          description="Turn off for STARTTLS on port 587."
        />
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">Incoming mail (IMAP) — to read replies</legend>
        <Switch
          checked={form.readReplies}
          onChange={(readReplies) => setForm({ ...form, readReplies })}
          label="Read replies from this mailbox"
          description="Hearth checks the inbox every minute and files each reply from a lead on that lead. It never marks, moves or deletes your email."
        />
        {form.readReplies && (
          <>
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem]">
              <Field label="Server" error={connect.errors.imapHost} required>
                <Input value={form.imapHost} onChange={(e) => setForm({ ...form, imapHost: e.target.value })} placeholder="imap.example.com" />
              </Field>
              <Field label="Port" error={connect.errors.imapPort} required>
                <Input type="number" value={form.imapPort} onChange={(e) => setForm({ ...form, imapPort: e.target.value })} />
              </Field>
            </div>
            <Switch checked={form.imapSecure} onChange={(imapSecure) => setForm({ ...form, imapSecure })} label="Use TLS (port 993)" description="Turn off only for a server on port 143 with STARTTLS." />
          </>
        )}
      </fieldset>

      <Switch checked={form.useForOutreach} onChange={(useForOutreach) => setForm({ ...form, useForOutreach })} label={OUTREACH_LABEL} description={OUTREACH_HINT} />
      <FormError message={connect.formError} hint="Check the servers, ports and password; your email provider’s help pages list the SMTP and IMAP settings." />
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" loading={connect.pending}>
          Sign in and connect
        </Button>
        <p className="text-xs text-slate-500">Hearth signs in once now to check, then stores the password encrypted.</p>
      </div>
    </form>
  );
}
